/**
 * React hook for Socket.IO connection management.
 */

import { useEffect, useState, useCallback, useRef, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import i18next from 'i18next'
import {
  connectSocket,
  disconnectSocket,
  onEvent,
  emitEvent,
  getSocket,
  SocketAuth,
  setSessionInvalidCallback,
  subscribeSocketStatus,
  getSocketConnectedSnapshot,
  getSocketConnectingSnapshot,
} from '../services/socket'
import { usePlayerStore } from '../stores/playerStore'

export type ReconnectState = "connected" | "reconnecting" | "failed"

export interface UseSocketReturn {
  isConnected: boolean
  isConnecting: boolean
  error: Error | null
  reconnectState: ReconnectState
  connect: () => Promise<void>
  disconnect: () => void
  on: <T = unknown>(event: string, handler: (data: T) => void) => () => void
  emit: <T = unknown>(event: string, data?: T) => void
}

export function useSocket(): UseSocketReturn {
  // Mirrors the socket service instead of duplicating its state per hook instance:
  // every useSocket() caller now observes the same connection status.
  const isConnected = useSyncExternalStore(
    subscribeSocketStatus,
    getSocketConnectedSnapshot
  )
  const isConnecting = useSyncExternalStore(
    subscribeSocketStatus,
    getSocketConnectingSnapshot
  )
  const [error, setError] = useState<Error | null>(null)
  const [reconnectState, setReconnectState] = useState<ReconnectState>("connected")
  const navigate = useNavigate()

  // Use playerStore for both playerId and session (use sessionCode as fallback if sessionUuid not set)
  const { playerId, sessionCode, sessionUuid } = usePlayerStore()
  const sessionId = sessionUuid || sessionCode

  // Track if we've attempted connection
  const hasAttempted = useRef(false)

  // Track socket instance to set up event listeners
  const [socketInstance, setSocketInstance] = useState<ReturnType<typeof getSocket>>(null)

  // Register session invalidation callback
  useEffect(() => {
    const handleSessionInvalid = (reason: string) => {
      console.error('[useSocket] Session invalid:', reason)
      
      // Clear player store
      usePlayerStore.getState().clear()
      
      // Disconnect socket
      disconnectSocket()
      hasAttempted.current = false

      // Navigate to home
      navigate('/', { replace: true })
    }

    setSessionInvalidCallback(handleSessionInvalid)

    return () => {
      setSessionInvalidCallback(null)
    }
  }, [navigate])

  // Reconnect progress. Connection status itself comes from the socket service
  // store above; only the manager-level reconnect phase is tracked here.
  useEffect(() => {
    const socket = socketInstance
    if (!socket) {
      return
    }

    // Reconnect events are on the Manager (socket.io), NOT on the socket itself
    // socket.io v4: reconnect_attempt, reconnect, reconnect_failed, reconnect_error
    const handleReconnectAttempt = () => {
      console.log('[useSocket] Reconnect attempt')
      setReconnectState('reconnecting')
    }
    const handleReconnect = () => {
      console.log('[useSocket] Reconnect succeeded')
      setReconnectState('connected')
    }
    const handleReconnectFailed = () => {
      console.log('[useSocket] Reconnect failed (all attempts exhausted)')
      setReconnectState('failed')
    }

    socket.io.on('reconnect_attempt', handleReconnectAttempt)
    socket.io.on('reconnect', handleReconnect)
    socket.io.on('reconnect_failed', handleReconnectFailed)

    return () => {
      socket.io.off('reconnect_attempt', handleReconnectAttempt)
      socket.io.off('reconnect', handleReconnect)
      socket.io.off('reconnect_failed', handleReconnectFailed)
    }
  }, [socketInstance])

  // Track last connected session to detect session changes
  const lastSessionRef = useRef<string | null>(null)

  // Auto-connect when credentials are available
  useEffect(() => {
    // Detect session change - reset hasAttempted if session changed
    if (sessionId !== lastSessionRef.current) {
      hasAttempted.current = false
      lastSessionRef.current = sessionId
      
      // Disconnect from old session if connected
      if (isConnected) {
        disconnectSocket()
      }
    }

    if (sessionId && playerId && !isConnected && !isConnecting && !hasAttempted.current) {
      hasAttempted.current = true

      const auth: SocketAuth = {
        session_id: sessionId,
        player_id: playerId,
      }

      connectSocket(auth)
        .then(() => {
          // Get and store the socket instance after successful connection
          const socket = getSocket()
          console.log('[useSocket] connectSocket succeeded, socket:', socket?.id)
          setSocketInstance(socket)
          setError(null)
        })
        .catch((err) => {
          setError(err instanceof Error ? err : new Error(i18next.t('game.connectionFailed')))
          hasAttempted.current = false // Allow retry
        })
    }
  }, [sessionId, playerId, isConnected, isConnecting])

  // Disconnect on unmount or when credentials cleared
  useEffect(() => {
    return () => {
      if (!sessionId || !playerId) {
        disconnectSocket()
        hasAttempted.current = false
      }
    }
  }, [sessionId, playerId])

  const connect = useCallback(async () => {
    if (!sessionId || !playerId) {
      throw new Error(i18next.t('game.missingSessionOrPlayer'))
    }

    const auth: SocketAuth = {
      session_id: sessionId,
      player_id: playerId,
    }

    setError(null)

    try {
      await connectSocket(auth)
      const socket = getSocket()
      console.log('[useSocket] Manual connect succeeded, socket:', socket?.id)
      setSocketInstance(socket)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(i18next.t('game.connectionFailed'))
      setError(error)
      throw error
    }
  }, [sessionId, playerId])

  const disconnect = useCallback(() => {
    disconnectSocket()
    hasAttempted.current = false
  }, [])

  // `on` - stable function; onEvent keeps the subscription across socket instances
  const on = useCallback(<T = unknown>(event: string, handler: (data: T) => void) => {
    return onEvent(event, handler)
  }, [])

  const emit = useCallback(<T = unknown>(event: string, data?: T) => {
    emitEvent(event, data)
  }, [])

  return {
    isConnected,
    isConnecting,
    error,
    reconnectState,
    connect,
    disconnect,
    on,
    emit,
  }
}
